import { z } from 'zod';

export function sanitizeCnpj(value: string): string {
  return value.replace(/\D/g, '');
}

export function formatCnpj(value: string): string {
  const digits = sanitizeCnpj(value).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

export function isValidCnpj(raw: string): boolean {
  const digits = sanitizeCnpj(raw);
  if (digits.length !== 14) return false;
  if (/^(\d)\1+$/.test(digits)) return false;

  const calc = (slice: string, weights: number[]): number => {
    const sum = slice
      .split('')
      .reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const d1 = calc(digits.slice(0, 12), w1);
  const d2 = calc(digits.slice(0, 12) + d1, w2);
  return d1 === Number(digits[12]) && d2 === Number(digits[13]);
}

const REGIMES = ['SIMPLES_III', 'SIMPLES_V', 'LUCRO_PRESUMIDO'] as const;
export type TaxRegime = (typeof REGIMES)[number];

export const cnpjSchema = z.object({
  cnpj: z
    .string()
    .transform(sanitizeCnpj)
    .refine((v) => v.length === 14, 'CNPJ deve ter 14 dígitos')
    .refine(isValidCnpj, 'CNPJ inválido'),
  razaoSocial: z.string().trim().min(2, 'Informe a razão social'),
  taxRegime: z.enum(REGIMES, { message: 'Selecione um regime tributário' }),
  cep: z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => v.length === 8, 'CEP deve ter 8 dígitos'),
  street: z.string().trim().min(1, 'Informe o logradouro'),
  number: z.string().trim().min(1, 'Informe o número'),
  complement: z.string().trim().optional(),
  district: z.string().trim().min(1, 'Informe o bairro'),
  city: z.string().trim().min(1, 'Informe a cidade'),
  state: z
    .string()
    .trim()
    .length(2, 'UF deve ter 2 letras')
    .transform((v) => v.toUpperCase()),
  municipalityCode: z
    .string()
    .trim()
    .regex(/^\d{1,10}$/, 'Código IBGE do município inválido'),
  inscricaoMunicipal: z
    .string()
    .trim()
    .min(1, 'Inscrição municipal é obrigatória para emitir NFS-e')
    .max(30, 'Inscrição municipal muito longa'),
  cnae: z
    .string()
    .trim()
    .optional()
    .refine(
      (v) => !v || /^\d{4}-?\d\/?\d{2}$/.test(v),
      'CNAE inválido (ex.: 8630-5/03 ou 8630503)',
    ),
});
export type CnpjInput = z.infer<typeof cnpjSchema>;

export const TAX_REGIME_LABELS: Record<TaxRegime, string> = {
  SIMPLES_III: 'Simples Nacional — Anexo III',
  SIMPLES_V: 'Simples Nacional — Anexo V',
  LUCRO_PRESUMIDO: 'Lucro Presumido',
};

export const phoneE164Schema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v.length === 10 || v.length === 11, 'Telefone inválido')
  .transform((v) => `+55${v}`);

export function formatPhoneBR(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) {
    return d
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return d
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}
